import { Injectable } from '@angular/core';
import {
  DocumentDetails,
  DocumentDetailsRaw,
  DocumentList,
  DocumentListRaw,
  DocumentListRequest,
  DocumentCreateRequest,
  DocumentCreateResponse,
} from '@shared/models/features/document';
import { constants } from '@shared/constants';
import { environment } from '@env';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError, of, map, catchError, BehaviorSubject, switchMap } from 'rxjs';
import { BackendService, StorageService } from '@shared/services';
import { CustomHttpParamEncoder } from '@shared/encoder';
import { documentTypeDisplayName, documentTypeIcon } from '@shared/utils';
import { FilterValues } from '@shared/models';

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  businessAccountDocumentFilter = new BehaviorSubject<FilterValues>({});

  constructor(
    private backendService: BackendService,
    private storageService: StorageService,
    private http: HttpClient,
  ) {}

  public setBusinessAccountDocumentFilterParams(filters: FilterValues): void {
    this.storageService.setItem(constants.KOR_BUSINESS_ACCOUNT_DOCUMENT_FILTER, filters);
    this.updateBusinessAccountDocumentFilterParams(filters);
  }

  public getBusinessAccountFilterParams(): void {
    const filters = this.storageService.getItem<FilterValues>(constants.KOR_BUSINESS_ACCOUNT_DOCUMENT_FILTER);

    if (filters && Object.keys(filters).length) {
      this.updateBusinessAccountDocumentFilterParams(filters);
    }
  }

  private updateBusinessAccountDocumentFilterParams(filters: FilterValues): void {
    this.businessAccountDocumentFilter.next(filters);
  }

  getBusinessAccountDocuments({ page, size, searchString, sortParams, activeFilters }: DocumentListRequest): Observable<DocumentList> {
    const pageSize = size ?? constants.TABLE_ROWS;
    let params = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set('page', `${page ?? 0}`).set('size', `${pageSize}`);

    if (activeFilters?.businessAccountId) {
      params = params.append('holderId', activeFilters.businessAccountId);
      params = params.append('holderType', 'BUSINESS_ACCOUNT');
    }

    if (searchString) {
      params = params.append('search', searchString);
    }

    if (activeFilters) {
      Object.keys(activeFilters).forEach((key) => {
        params = params.append(key, activeFilters[key]!);
      });
    }

    if (sortParams) {
      params = params.append('sort', `${sortParams.key},${sortParams.sortDir}`);
    }

    return this.backendService.get<DocumentListRaw>(`${environment.documentFlowEndpoint}`, { params }).pipe(
      map((response) => this.mapDocumentList(response)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  getBeneficiaryDocuments({
    page,
    size,
    searchString,
    sortParams,
    activeFilters,
    beneficiaryId,
  }: DocumentListRequest & { beneficiaryId?: string }): Observable<DocumentList> {
    const pageSize = size ?? constants.TABLE_ROWS;
    let params = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set('page', `${page ?? 0}`).set('size', `${pageSize}`);

    if (beneficiaryId) {
      params = params.append('holderId', beneficiaryId);
      params = params.append('holderType', 'BENEFICIARY');
    }

    if (searchString) {
      params = params.append('search', searchString);
    }

    if (activeFilters) {
      Object.keys(activeFilters).forEach((key) => {
        params = params.append(key, activeFilters[key]!);
      });
    }

    if (sortParams) {
      params = params.append('sort', `${sortParams.key},${sortParams.sortDir}`);
    }

    return this.backendService.get<DocumentListRaw>(`${environment.documentFlowEndpoint}`, { params }).pipe(
      map((response) => this.mapDocumentList(response)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  mapDocumentList(response: DocumentListRaw): DocumentList {
    const { content = [], totalElements = 0, hasNext = false } = response;
    const items = content.map((item) => ({ ...item, documentTypeDisplayName: documentTypeDisplayName(item.documentType) }));

    return {
      items,
      totalElements,
      hasNext,
    };
  }

  getDocumentById(documentId: string, businessAccountId?: string): Observable<DocumentDetails> {
    const params = businessAccountId ? new HttpParams().set('businessAccountId', businessAccountId) : undefined;
    return this.backendService.get<DocumentDetailsRaw>(`${environment.documentFlowEndpoint}/${documentId}`, { params }).pipe(
      map((response) => ({
        ...response,
        documentTypeDisplayName: documentTypeDisplayName(response.documentType),
        documentTypeIcon: documentTypeIcon(response.documentType),
      })),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  deleteDocument(documentId: string, businessAccountId?: string): Observable<void> {
    const query = businessAccountId ? `?businessAccountId=${businessAccountId}` : '';
    return this.backendService
      .delete<void>(`${environment.documentFlowEndpoint}/${documentId}${query}`)
      .pipe(catchError((errorRes) => throwError(() => errorRes)));
  }

  updateDocument(
    documentId: string,
    documentData: Partial<DocumentCreateRequest> & { file?: ArrayBuffer | null; extension?: string; contentLength?: number },
    businessAccountId?: string,
  ): Observable<boolean> {
    const { file, ...updateData } = documentData;
    const params = businessAccountId ? new HttpParams().set('businessAccountId', businessAccountId) : undefined;
    return this.backendService
      .update<DocumentCreateResponse>(`${environment.documentFlowEndpoint}/${documentId}`, updateData, { params })
      .pipe(
        switchMap((response) => {
          if (!file) return of(true);
          return this.uploadDocumentFile({ url: response.url.value, file, extension: response.extension });
        }),
        map(() => true),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  createDocument(documentData: DocumentCreateRequest, file: ArrayBuffer): Observable<boolean> {
    return this.backendService.post<DocumentCreateResponse>(`${environment.documentFlowEndpoint}`, documentData).pipe(
      switchMap((response) => this.uploadDocumentFile({ url: response.url.value, file, extension: response.extension })),
      map(() => true),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  private uploadDocumentFile({ url, file, extension }: { url: string; file: ArrayBuffer; extension: string }): Observable<boolean> {
    const contentTypes: Record<string, string> = {
      JPG: 'image/jpeg',
      JPEG: 'image/jpeg',
      PNG: 'image/png',
      PDF: 'application/pdf',
    };
    const headers = new HttpHeaders({ 'Content-Type': contentTypes[extension.toUpperCase()] });
    return this.http.put(url, file, { headers }).pipe(
      map(() => true),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }
}
